import React, { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import {
  LeadStatusConfig,
  leadStatusConfigService,
} from '../services/leadConfigService';

/**
 * The tenant's lead statuses.
 *
 * Leads carried a native Postgres enum until they became generic, so every
 * status a tenant wanted was a migration. This is where the vocabulary lives
 * now - the lead twin of PatientStatusConfigPage.
 *
 * The three the IL flow ships with (CREATED / MATCHED / NOT_INTERESTED) can be
 * retitled freely, but their KEYS are compared as literals by the referral
 * ladder and by the agent API Israel calls, so the form warns before one of
 * those is renamed rather than silently detaching the ladder from the data.
 */

const IL_RESERVED_KEYS = new Set(['CREATED', 'MATCHED', 'NOT_INTERESTED']);

export const LeadStatusConfigPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [statuses, setStatuses] = useState<LeadStatusConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isDefault, setIsDefault] = useState(false);
  const [originalKey, setOriginalKey] = useState('');

  const loadStatuses = useCallback(async () => {
    if (!currentTenant) return;
    try {
      setIsLoading(true);
      setStatuses(await leadStatusConfigService.getAll(currentTenant.id));
      setError(null);
    } catch (err) {
      console.error('Failed to load lead statuses:', err);
      setError('Failed to load lead statuses');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setKey('');
    setTitle('');
    setSortOrder(0);
    setIsDefault(false);
    setOriginalKey('');
  };

  const handleAdd = () => {
    resetForm();
    setIsAdding(true);
    setSortOrder(statuses.length);
  };

  const handleEdit = (status: LeadStatusConfig) => {
    setIsAdding(false);
    setEditingId(status.id);
    setKey(status.key);
    setOriginalKey(status.key);
    setTitle(status.title);
    setSortOrder(status.sort_order);
    setIsDefault(status.is_default);
  };

  const handleSave = async () => {
    if (!currentTenant || !key.trim() || !title.trim()) return;

    // Renaming one of the IL keys detaches the referral ladder from the data
    // without anything failing - the filter simply stops matching.
    if (editingId && IL_RESERVED_KEYS.has(originalKey) && key.trim() !== originalKey) {
      const confirmed = window.confirm(
        `"${originalKey}" is used by name by the HMO referral flow. Renaming the key will ` +
        `stop the referral filters matching these leads. Rename it anyway?`,
      );
      if (!confirmed) return;
    }

    const payload = {
      key: key.trim(),
      title: title.trim(),
      sort_order: sortOrder,
      is_default: isDefault,
    };

    try {
      if (isAdding) {
        await leadStatusConfigService.create(currentTenant.id, payload);
      } else if (editingId) {
        await leadStatusConfigService.update(editingId, payload);
      }
      await loadStatuses();
      resetForm();
    } catch (err) {
      console.error('Failed to save lead status:', err);
      setError('Failed to save lead status');
    }
  };

  const handleDelete = async (status: LeadStatusConfig) => {
    const warning = IL_RESERVED_KEYS.has(status.key)
      ? `"${status.key}" is used by name by the HMO referral flow. Deleting it will leave ` +
        `existing leads showing a raw key instead of a title. Delete anyway?`
      : 'Are you sure you want to delete this lead status?';
    if (!window.confirm(warning)) return;

    try {
      await leadStatusConfigService.delete(status.id);
      await loadStatuses();
    } catch (err) {
      console.error('Failed to delete lead status:', err);
      setError('Failed to delete lead status');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Lead Statuses</h1>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            + Add Lead Status
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {(isAdding || editingId) && (
          <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">
              {isAdding ? 'Add New Lead Status' : 'Edit Lead Status'}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. CONTACTED"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. Contacted"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="leadStatusIsDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor="leadStatusIsDefault" className="text-sm font-medium text-gray-700">
                Default status for new leads
              </label>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Default</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {statuses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No lead statuses found. Click "Add Lead Status" to create one.
                  </td>
                </tr>
              ) : (
                statuses.map((status) => (
                  <tr key={status.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {status.key}
                      {IL_RESERVED_KEYS.has(status.key) && (
                        <span
                          className="ml-2 text-xs text-gray-500"
                          title="Used by name by the HMO referral flow"
                        >
                          (referral)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{status.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{status.sort_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{status.is_default ? 'Yes' : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => handleEdit(status)} className="text-teal-600 hover:text-teal-900 mr-4">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(status)} className="text-red-600 hover:text-red-900">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadStatusConfigPage;
